import { useCallback, useEffect, useState } from 'react';
import type { Code, VoteKind } from '../../shared/types';
import { castVote, fetchCodes } from '../lib/api';
import { readVotes, rememberVote } from '../lib/device';

interface CodesState {
  codes: Code[];
  lastConfirmedAt: string | null;
  loading: boolean;
  error: string | null;
  /** How this browser has voted, by code id. */
  myVotes: Record<string, VoteKind>;
  vote(codeId: string, vote: VoteKind): Promise<void>;
  retry(): void;
}

/** Refresh interval while the tab is visible, so vote counts stay live-ish. */
const POLL_MS = 60_000;

export function useCodes(): CodesState {
  const [codes, setCodes] = useState<Code[]>([]);
  const [lastConfirmedAt, setLastConfirmedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [myVotes, setMyVotes] = useState<Record<string, VoteKind>>(() => readVotes());
  const [attempt, setAttempt] = useState(0);

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      const data = await fetchCodes();
      if (signal?.aborted) return;
      setCodes(data.codes);
      setLastConfirmedAt(data.lastConfirmedAt);
      setError(null);
    } catch (err) {
      if (signal?.aborted) return;
      setError(err instanceof Error ? err.message : 'Could not load the codes.');
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    // The list lives on the server; fetching it is exactly the external
    // synchronisation an effect is for. Nothing here sets state synchronously —
    // every setState runs after the request resolves — but the rule cannot see
    // through the async boundary.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load(controller.signal);

    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') void load(controller.signal);
    }, POLL_MS);

    return () => {
      controller.abort();
      clearInterval(timer);
    };
  }, [load, attempt]);

  const vote = useCallback(async (codeId: string, kind: VoteKind) => {
    // Optimistic: the button should feel instant, and a failed report is not
    // worth interrupting anyone over.
    setMyVotes((prev) => ({ ...prev, [codeId]: kind }));
    rememberVote(codeId, kind);

    try {
      const result = await castVote(codeId, kind);
      // Functional form so a poll that landed while the request was in flight
      // keeps its fresher data for every other code.
      setCodes((current) =>
        current.map((code) =>
          code.id === codeId ? { ...code, votes: result.votes, verdict: result.verdict } : code
        )
      );
    } catch {
      // Leave the button showing the choice; the next poll reconciles counts.
    }
  }, []);

  const retry = useCallback(() => {
    setLoading(true);
    setAttempt((n) => n + 1);
  }, []);

  return { codes, lastConfirmedAt, loading, error, myVotes, vote, retry };
}
